<template>
  <div>
    <div class="mb-3 d-flex justify-content-between">
      <div
        class="btn-group"
        role="group"
      >
        <button
          class="btn"
          :class="mode === 'add' ? 'btn-primary' : 'btn-outline-primary'"
          @click="mode = 'add'"
        >
          <i class="fa fa-plus" />
          {{ $t('shop.offer.addMonths') }}
        </button>
        <button
          class="btn"
          :class="mode === 'remove' ? 'btn-danger' : 'btn-outline-danger'"
          @click="mode = 'remove'"
        >
          <i class="fa fa-minus" />
          {{ $t('shop.offer.removeMonths') }}
        </button>
      </div>

      <div>
        <button
          class="mx-2 btn btn-sm btn-primary"
          @click="selectAll"
        >
          {{ $t('groups.selectAll') }}
        </button>
        <button
          class="mx-2 btn btn-sm btn-outline-primary"
          @click="clearDays"
        >
          {{ $t('shop.offer.clear') }}
        </button>
      </div>
    </div>
    <hr>

    <div style="max-width: 500px; margin: 0 auto">
      <YearMonthCalendar
        ref="calendar"
        :class="mode === 'remove' ? 'remove-highlight' : ''"
        :day-status="dayStatus"
        all-available
        :highlighted="mode === 'remove' ? removedDays : addedDays"
        @highlighted="handleDaySelection"
        @update:dates="requestCalendar"
      />
    </div>
    <hr>

    <!-- Summary Section -->
    <div class="mt-4">
      <div v-if="addedDays.length > 0 || removedDays.length > 0">
        <div
          v-if="addedDays.length > 0"
          class="mb-2"
        >
          <span class="text-primary font-weight-bold">{{ $t('shop.offer.addedMonths') }}:</span>
          <span class="ml-2">
            <span
              v-for="day in addedDays"
              :key="day"
              class="badge bg-primary text-white small"
              style="padding-left: 6px; padding-right: 6px; margin-right: 3px"
            >
              {{ formatMonth(day) }}
            </span>
          </span>
        </div>

        <div v-if="removedDays.length > 0">
          <span class="text-danger font-weight-bold">{{ $t('shop.offer.removedMonths') }}:</span>
          <span class="ml-2">
            <span
              v-for="day in removedDays"
              :key="day"
              class="badge bg-danger text-white"
              style="padding-left: 6px; padding-right: 6px; margin-right: 3px"
            >
              {{ formatMonth(day) }}
            </span>
          </span>
        </div>
      </div>
      <div
        v-else
        class="text-center text-muted"
      >
        <i class="far fa-calendar-days" />
        <div class="text-center small mt-2">
          {{ $t('shop.offer.selectDaysToAssignOrUnassignOffer') }}
        </div>
      </div>
    </div>
    <hr>
    <div class="d-flex justify-content-end">
      <div class="d-flex align-items-center">
        <SwitchWithDescription
          v-if="removedDays.length > 0"
          title="Usuń zamówienia"
          description="Zamówienia złożone na wybraną ofertę zostaną usunięte w te dni"
          :values="cancelOrders"
          style="width: 400px;"
          class="mr-3"
          @switch="cancelOrders = !cancelOrders"
        />

        <button
          class="btn btn-primary px-5"
          @click="save"
        >
          {{ $t('general.save') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import SwitchWithDescription from '@/components/settings/SwitchWithDescription';
import YearMonthCalendar from '@/modules/payer/components/shop/YearMonthCalendar';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';

export default {
  props: {
    catalogId: String,
    listingIds: Array,
  },
  data: () => ({
    selectedMonth: null,
    calendar: [],
    selectedDays: [],
    cancelOrders: false,
    mode: 'add',
    addedDays: [],
    removedDays: [],
    currentFrom: null,
    currentTo: null,
  }),
  components: {
    SwitchWithDescription,
    YearMonthCalendar,
  },
  computed: {
    ...mapGetters('shop', [
      'dailyOrderSchema',
      'monthlyOrderSchema',
    ]),
    dayStatus() {
      const status = {};
      // Mark added days with +1
      this.addedDays.forEach((day) => {
        status[day] = { new: 1, paid: 0 };
      });
      // Mark removed days with -1
      this.removedDays.forEach((day) => {
        status[day] = { cancel: 1, paid: 0 };
      });

      this.calendar
        .filter((x) => this.listingIds.includes(x.listingId))
        .forEach((day) => {
          if (!status[day.day]) {
            status[day.day] = {
              paid: 1,
            };
          } else {
            status[day.day].paid += 1;
          }
        });

      return status;
    },
  },
  methods: {
    ...mapActions('shop', [
      'getListingsCalendar',
      'updateListingCalendar',
    ]),
    save() {
      this.updateListingCalendar({
        params: {
          catalogId: this.catalogId,
        },
        data: {
          listingIds: this.listingIds,
          added: this.addedDays,
          removed: this.removedDays,
          cancelOrders: this.cancelOrders,
        },
      })
        .then(() => {
          this.$emit('refresh');
          this.$emit('close');
        });
    },
    clearDays() {
      this.addedDays = [];
      this.removedDays = [];
    },
    requestCalendar({ from, to }) {
      this.currentFrom = from;
      this.currentTo = to;

      this.selectedMonth = new Date(from);
      this.getListingsCalendar({
        params: {
          catalogId: this.catalogId,
          query: {
            from,
            to,
          },
        },
      })
        .then(({ data }) => {
          this.calendar = data;
        });
    },
    handleDaySelection({
      list, from, to,
    }) {
      if (this.mode === 'add') {
        // Get the days that were deselected (in previous selection but not in current)
        const deselectedDays = this.addedDays
          .filter((day) => !list.includes(day)
              && moment(day).isBetween(from, to, 'day', '[)'));

        // Remove deselected days from addedDays
        deselectedDays.forEach((day) => {
          const index = this.addedDays.indexOf(day);
          if (index > -1) {
            this.addedDays.splice(index, 1);
          }
        });

        // Add newly selected days
        list.forEach((day) => {
          if (!this.addedDays.includes(day)) {
            this.addedDays.push(day);
            // Remove from removedDays if present
            const removedIndex = this.removedDays.indexOf(day);
            if (removedIndex > -1) {
              this.removedDays.splice(removedIndex, 1);
            }
          }
        });

        this.addedDays = this.addedDays.sort((a, b) => moment(a).diff(moment(b)));
      } else {
        // Get the days that were deselected
        const deselectedDays = this.removedDays
          .filter((day) => !list.includes(day)
        && moment(day).isBetween(from, to, 'day', '[)'));

        // Remove deselected days from removedDays
        deselectedDays.forEach((day) => {
          const index = this.removedDays.indexOf(day);
          if (index > -1) {
            this.removedDays.splice(index, 1);
          }
        });

        // Add newly selected days
        list.forEach((day) => {
          if (!this.removedDays.includes(day)) {
            this.removedDays.push(day);
            // Remove from addedDays if present
            const addedIndex = this.addedDays.indexOf(day);
            if (addedIndex > -1) {
              this.addedDays.splice(addedIndex, 1);
            }
          }
        });
      }
    },
    getDaysInMonth() {
      const curr = moment(this.currentFrom, 'YYYY-MM-DD');
      const end = moment(this.currentTo, 'YYYY-MM-DD');

      const days = [];
      while (curr.isBefore(end)) {
        days.push(curr.format('YYYY-MM-DD'));
        curr.add(1, 'day');
      }

      return days;
    },
    selectAll() {
      const year = moment(this.currentFrom).year();
      this.addedDays = Array.from({ length: 12 }, (_, i) =>
        `${year}-${(i + 1).toString().padStart(2, '0')}-01`);
    },
    formatMonth(dateString) {
      return moment(dateString).format('MMMM YYYY');
    },
  },
};
</script>

<style lang="scss" scoped>
.gap-2 {
  gap: 0.5rem;
}
.mr-1 {
  margin-right: 0.25rem;
}

.remove-highlight ::v-deep .day-box {
  &.highlighted {
    .day {
      border: 2px solid $red;
    }
  }
}
</style>
